<template>
  <div class="page-content">
    <div class="page-header">
      <h1 class="page-title">Omat ryhmät</h1>
      <p class="page-description">Kuulu useaan ryhmään, vaihda niiden välillä ja seuraa jokaisen ryhmän tuloksia.</p>
    </div>

    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <span class="loading-text">Ladataan ryhmätietoja...</span>
    </div>

    <div v-else>
      <div v-if="error" class="error-message">
        <strong>Virhe!</strong>
        <span>{{ error }}</span>
      </div>

      <div v-if="!isAuthenticated" class="no-group-content">
        <p class="info-text">Kirjaudu sisään hallinnoidaksesi ryhmiäsi.</p>
      </div>

      <div v-else>
        <div class="group-summary-card">
          <div>
            <h2>Ryhmät käytössä</h2>
            <p>{{ groupMemberships.length }} / {{ maxGroups }} ryhmää</p>
          </div>
          <span v-if="!canJoinMoreGroups" class="limit-pill">Ryhmäraja täynnä</span>
        </div>

        <div class="group-shell">
          <aside v-if="hasGroups" class="group-sidebar">
            <div class="group-sidebar-header">
              <h2>Ryhmät</h2>
              <span class="group-count">{{ groupMemberships.length }}</span>
            </div>

            <div class="group-list">
              <button
                v-for="group in groupMemberships"
                :key="group.GroupID"
                type="button"
                class="group-list-item"
                :class="{ selected: selectedGroupId === group.GroupID }"
                @click="selectGroup(group.GroupID)"
              >
                <span class="group-list-name">{{ group.GroupName }}</span>
                <span class="group-list-meta">
                  <span class="role-badge" :class="group.Role === 'owner' ? 'owner' : 'member'">
                    {{ group.Role === 'owner' ? 'Omistaja' : 'Jäsen' }}
                  </span>
                </span>
              </button>
            </div>
          </aside>

          <div class="group-main">
            <div v-if="selectedGroup" class="group-content">
              <div class="leaderboard-section">
                <div class="leaderboard-header leaderboard-header-with-meta">
                  <div>
                    <h2>{{ selectedGroup.GroupName || 'Ryhmän tulokset' }}</h2>
                    <p class="leaderboard-subtitle">
                      {{ selectedGroup.Role === 'owner' ? 'Omistat tämän ryhmän.' : 'Olet tämän ryhmän jäsen.' }}
                    </p>
                  </div>
                  <button
                    @click="handleLeaveGroup"
                    :disabled="isSubmitting"
                    class="action-button danger compact"
                  >
                    <span v-if="isSubmitting">Poistutaan...</span>
                    <span v-else>Poistu ryhmästä</span>
                  </button>
                </div>

                <div v-if="isLeaderboardLoading" class="loading-container">
                  <div class="loading-spinner"></div>
                  <span class="loading-text">Ladataan tuloksia...</span>
                </div>

                <div v-if="leaderboardError" class="error-message embedded-error">
                  <strong>Virhe!</strong>
                  <span>{{ leaderboardError }}</span>
                </div>

                <div v-if="!isLeaderboardLoading && !leaderboardError && groupLeaderboard.length > 0" class="table-container">
                  <table class="leaderboard-table">
                    <thead>
                      <tr>
                        <th class="rank-column">Sija</th>
                        <th class="player-column">Pelaaja</th>
                        <th class="points-column">Pisteet</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(player, index) in groupLeaderboard" :key="player.PlayerID || player.username" class="leaderboard-row">
                        <td class="rank-column">{{ index + 1 }}</td>
                        <td class="player-column">
                          <span
                            v-if="player.PlayerID"
                            class="player-name clickable"
                            @click="openPlayerTeamModal(player.PlayerID, player.username)"
                            :title="'Näytä ' + player.username + ' remmi'"
                          >
                            {{ player.username }}
                          </span>
                          <span v-else class="player-name disabled" title="Pelaajan tietoja ei saatavilla">
                            {{ player.username }}
                          </span>
                        </td>
                        <td class="points-column">{{ player.total_points }} p</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="!isLeaderboardLoading && !leaderboardError && groupLeaderboard.length === 0" class="empty-state">
                  <p class="empty-text">Ei tuloksia saatavilla tälle ryhmälle.</p>
                </div>
              </div>
            </div>

            <div v-else class="no-group-content empty-panel">
              <p class="info-text">Et ole tällä hetkellä missään ryhmässä. Luo uusi ryhmä tai liity olemassa olevaan ryhmään.</p>
            </div>

            <div class="group-actions">
              <div v-if="limitMessage" class="limit-message">
                {{ limitMessage }}
              </div>

              <div class="action-card">
                <button
                  @click="showCreateForm = !showCreateForm"
                  class="action-toggle"
                  :disabled="!canJoinMoreGroups"
                >
                  <span class="toggle-icon">{{ showCreateForm ? '▼' : '►' }}</span>
                  <span class="toggle-text">Luo uusi ryhmä</span>
                </button>

                <div v-if="showCreateForm" class="action-form">
                  <form @submit.prevent="handleCreateGroup">
                    <div class="form-group">
                      <label for="create-group-name">Ryhmän nimi:</label>
                      <input
                        id="create-group-name"
                        type="text"
                        v-model="createGroupName"
                        required
                        minlength="3"
                        maxlength="50"
                        placeholder="esim. Mestarit"
                        :disabled="!canJoinMoreGroups || isSubmitting"
                      />
                    </div>
                    <div class="form-group">
                      <label for="create-group-password">Ryhmän salasana:</label>
                      <input
                        id="create-group-password"
                        type="password"
                        v-model="createGroupPassword"
                        required
                        minlength="6"
                        placeholder="Vähintään 6 merkkiä"
                        :disabled="!canJoinMoreGroups || isSubmitting"
                      />
                    </div>
                    <div class="form-actions">
                      <button
                        type="submit"
                        :disabled="!canJoinMoreGroups || isSubmitting"
                        class="submit-button create"
                      >
                        <span v-if="isSubmitting">Luodaan...</span>
                        <span v-else>Luo ryhmä</span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              <div class="action-card">
                <button
                  @click="showJoinForm = !showJoinForm"
                  class="action-toggle"
                  :disabled="!canJoinMoreGroups"
                >
                  <span class="toggle-icon">{{ showJoinForm ? '▼' : '►' }}</span>
                  <span class="toggle-text">Liity olemassa olevaan ryhmään</span>
                </button>

                <div v-if="showJoinForm" class="action-form">
                  <form @submit.prevent="handleJoinGroup">
                    <div class="form-group">
                      <label for="join-group-name">Ryhmän nimi:</label>
                      <input
                        id="join-group-name"
                        type="text"
                        v-model="joinGroupName"
                        required
                        placeholder="Syötä tarkka ryhmän nimi"
                        :disabled="!canJoinMoreGroups || isSubmitting"
                      />
                    </div>
                    <div class="form-group">
                      <label for="join-group-password">Ryhmän salasana:</label>
                      <input
                        id="join-group-password"
                        type="password"
                        v-model="joinGroupPassword"
                        required
                        placeholder="Syötä ryhmän salasana"
                        :disabled="!canJoinMoreGroups || isSubmitting"
                      />
                    </div>
                    <div class="form-actions">
                      <button
                        type="submit"
                        :disabled="!canJoinMoreGroups || isSubmitting"
                        class="submit-button join"
                      >
                        <span v-if="isSubmitting">Liitytään...</span>
                        <span v-else>Liity ryhmään</span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <PlayerTeamModal
        :show="isPlayerTeamModalVisible"
        :player-id="selectedPlayerId"
        :player-name="selectedPlayerName"
        @close="closePlayerTeamModal"
        @viewHorse="handleViewHorse"
        @viewJockey="handleViewJockey"
      />
    </Teleport>

    <Teleport to="body">
      <HorsePerformanceModal
        :show="isHorsePerformanceModalVisible"
        :horse-id="selectedHorseId"
        :horse-name="selectedHorseName"
        @close="closeHorsePerformanceModal"
      />
    </Teleport>

    <Teleport to="body">
      <JockeyPerformanceModal
        :show="isJockeyPerformanceModalVisible"
        :jockey-id="selectedJockeyId"
        :jockey-name="selectedJockeyName"
        @close="closeJockeyPerformanceModal"
      />
    </Teleport>
  </div>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from 'vue';
import axios from '../plugins/axios';
import PlayerTeamModal from './PlayerTeamModal.vue';
import HorsePerformanceModal from './HorsePerformanceModal.vue';
import JockeyPerformanceModal from './JockeyPerformanceModal.vue';

export default {
  name: 'OwnGroupPage',
  components: {
    PlayerTeamModal,
    HorsePerformanceModal,
    JockeyPerformanceModal,
  },
  setup() {
    const isAuthenticated = inject('isAuthenticated');

    const isLoading = ref(true);
    const isLeaderboardLoading = ref(false);
    const isSubmitting = ref(false);
    const error = ref(null);
    const leaderboardError = ref(null);
    const groupMemberships = ref([]);
    const selectedGroupId = ref(null);
    const groupLeaderboard = ref([]);
    const maxGroups = ref(30);

    const showCreateForm = ref(false);
    const showJoinForm = ref(false);
    const createGroupName = ref('');
    const createGroupPassword = ref('');
    const joinGroupName = ref('');
    const joinGroupPassword = ref('');

    const isPlayerTeamModalVisible = ref(false);
    const selectedPlayerId = ref(null);
    const selectedPlayerName = ref('');
    const isHorsePerformanceModalVisible = ref(false);
    const selectedHorseId = ref(null);
    const selectedHorseName = ref('');
    const isJockeyPerformanceModalVisible = ref(false);
    const selectedJockeyId = ref(null);
    const selectedJockeyName = ref('');

    const apiBaseUrl = computed(() => process.env.VUE_APP_API_BASE_URL);
    const hasGroups = computed(() => groupMemberships.value.length > 0);
    const canJoinMoreGroups = computed(() => groupMemberships.value.length < maxGroups.value);
    const selectedGroup = computed(
      () => groupMemberships.value.find((group) => group.GroupID === selectedGroupId.value) || null,
    );
    const limitMessage = computed(() => {
      if (canJoinMoreGroups.value) {
        return '';
      }
      return `Voit kuulua enintään ${maxGroups.value} ryhmään samaan aikaan.`;
    });

    const clearPageState = () => {
      error.value = null;
      leaderboardError.value = null;
      groupMemberships.value = [];
      selectedGroupId.value = null;
      groupLeaderboard.value = [];
      showCreateForm.value = false;
      showJoinForm.value = false;
      createGroupName.value = '';
      createGroupPassword.value = '';
      joinGroupName.value = '';
      joinGroupPassword.value = '';
      closePlayerTeamModal();
      closeHorsePerformanceModal();
      closeJockeyPerformanceModal();
    };

    const fetchGroupLeaderboard = async (groupId) => {
      if (!groupId) {
        groupLeaderboard.value = [];
        return;
      }
      isLeaderboardLoading.value = true;
      leaderboardError.value = null;
      groupLeaderboard.value = [];
      try {
        const response = await axios.get(`${apiBaseUrl.value}/groups/${groupId}/leaderboard`);
        if (response.data && Array.isArray(response.data.leaderboard)) {
          groupLeaderboard.value = response.data.leaderboard;
        } else {
          leaderboardError.value = 'Odottamaton tietomuoto tuloslistalle.';
        }
      } catch (err) {
        leaderboardError.value = err.response?.data?.message || 'Ryhmän tulosten lataaminen epäonnistui.';
      } finally {
        isLeaderboardLoading.value = false;
      }
    };

    const refreshGroups = async (preferredGroupId = null) => {
      isLoading.value = true;
      error.value = null;
      leaderboardError.value = null;
      if (!isAuthenticated.value) {
        clearPageState();
        isLoading.value = false;
        return;
      }

      try {
        const response = await axios.get(`${apiBaseUrl.value}/groups/mine`);
        const groups = Array.isArray(response.data?.groups) ? response.data.groups : [];
        groupMemberships.value = groups;
        maxGroups.value = response.data?.maxGroups || 30;

        const desiredGroupId =
          (preferredGroupId && groups.find((group) => group.GroupID === preferredGroupId)?.GroupID) ||
          (selectedGroupId.value && groups.find((group) => group.GroupID === selectedGroupId.value)?.GroupID) ||
          groups[0]?.GroupID ||
          null;

        selectedGroupId.value = desiredGroupId;
        if (desiredGroupId) {
          await fetchGroupLeaderboard(desiredGroupId);
        } else {
          groupLeaderboard.value = [];
        }
      } catch (err) {
        error.value = err.response?.data?.message || 'Ryhmätietojen lataaminen epäonnistui.';
        groupMemberships.value = [];
        selectedGroupId.value = null;
        groupLeaderboard.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const selectGroup = async (groupId) => {
      if (!groupId || selectedGroupId.value === groupId) {
        return;
      }
      selectedGroupId.value = groupId;
      await fetchGroupLeaderboard(groupId);
    };

    const handleCreateGroup = async () => {
      isSubmitting.value = true;
      error.value = null;
      const requestedName = createGroupName.value.trim();
      try {
        const response = await axios.post(`${apiBaseUrl.value}/groups`, {
          GroupName: requestedName,
          Password: createGroupPassword.value,
        });
        alert(`Ryhmä '${requestedName}' luotu onnistuneesti!`);
        createGroupName.value = '';
        createGroupPassword.value = '';
        showCreateForm.value = false;
        await refreshGroups(response.data?.GroupID || null);
      } catch (err) {
        const errorMsg = err.response?.data?.message || 'Ryhmän luominen epäonnistui.';
        error.value = errorMsg;
        alert(`Virhe: ${errorMsg}`);
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleJoinGroup = async () => {
      isSubmitting.value = true;
      error.value = null;
      const requestedName = joinGroupName.value.trim();
      try {
        const response = await axios.post(`${apiBaseUrl.value}/groups/join`, {
          GroupName: requestedName,
          Password: joinGroupPassword.value,
        });
        alert(`Liityit onnistuneesti ryhmään '${requestedName}'!`);
        joinGroupName.value = '';
        joinGroupPassword.value = '';
        showJoinForm.value = false;
        await refreshGroups(response.data?.GroupID || null);
      } catch (err) {
        const errorMsg = err.response?.data?.message || 'Ryhmään liittyminen epäonnistui.';
        error.value = errorMsg;
        alert(`Virhe: ${errorMsg}`);
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleLeaveGroup = async () => {
      if (!selectedGroup.value?.GroupID) {
        return;
      }
      if (!confirm(`Haluatko varmasti poistua ryhmästä '${selectedGroup.value.GroupName}'?`)) {
        return;
      }
      isSubmitting.value = true;
      error.value = null;
      const leavingGroupId = selectedGroup.value.GroupID;
      try {
        await axios.delete(`${apiBaseUrl.value}/groups/${leavingGroupId}/members/me`);
        alert('Poistuit onnistuneesti ryhmästä.');
        const remainingGroups = groupMemberships.value.filter((group) => group.GroupID !== leavingGroupId);
        const nextGroupId = remainingGroups[0]?.GroupID || null;
        await refreshGroups(nextGroupId);
      } catch (err) {
        const errorMsg = err.response?.data?.message || 'Ryhmästä poistuminen epäonnistui.';
        error.value = errorMsg;
        alert(`Virhe: ${errorMsg}`);
      } finally {
        isSubmitting.value = false;
      }
    };

    const openPlayerTeamModal = (playerId, playerName) => {
      if (!playerId) {
        return;
      }
      selectedPlayerId.value = playerId;
      selectedPlayerName.value = playerName || 'Selected Player';
      isPlayerTeamModalVisible.value = true;
      closeHorsePerformanceModal();
      closeJockeyPerformanceModal();
    };

    const closePlayerTeamModal = () => {
      isPlayerTeamModalVisible.value = false;
    };

    const openHorsePerformanceModal = (horseId, horseName) => {
      if (!horseId) {
        return;
      }
      selectedHorseId.value = horseId;
      selectedHorseName.value = horseName || 'Selected Horse';
      isHorsePerformanceModalVisible.value = true;
      closeJockeyPerformanceModal();
    };

    const closeHorsePerformanceModal = () => {
      isHorsePerformanceModalVisible.value = false;
      selectedHorseId.value = null;
      selectedHorseName.value = '';
    };

    const openJockeyPerformanceModal = (jockeyId, jockeyName) => {
      if (!jockeyId) {
        return;
      }
      selectedJockeyId.value = jockeyId;
      selectedJockeyName.value = jockeyName || 'Selected Jockey';
      isJockeyPerformanceModalVisible.value = true;
      closeHorsePerformanceModal();
    };

    const closeJockeyPerformanceModal = () => {
      isJockeyPerformanceModalVisible.value = false;
      selectedJockeyId.value = null;
      selectedJockeyName.value = '';
    };

    const handleViewHorse = (payload) => {
      if (payload && payload.horseId) {
        openHorsePerformanceModal(payload.horseId, payload.horseName);
      }
    };

    const handleViewJockey = (payload) => {
      if (payload && payload.jockeyId) {
        openJockeyPerformanceModal(payload.jockeyId, payload.jockeyName);
      }
    };

    onMounted(() => {
      if (isAuthenticated.value) {
        refreshGroups();
      } else {
        isLoading.value = false;
        error.value = 'Kirjaudu sisään hallinnoidaksesi ryhmiäsi.';
      }
    });

    watch(isAuthenticated, (newValue, oldValue) => {
      if (newValue === true && oldValue === false) {
        refreshGroups();
      } else if (newValue === false) {
        clearPageState();
        isLoading.value = false;
        error.value = 'Kirjaudu sisään hallinnoidaksesi ryhmiäsi.';
      }
    });

    return {
      isAuthenticated,
      isLoading,
      isLeaderboardLoading,
      isSubmitting,
      error,
      leaderboardError,
      groupMemberships,
      selectedGroupId,
      selectedGroup,
      groupLeaderboard,
      maxGroups,
      hasGroups,
      canJoinMoreGroups,
      limitMessage,
      showCreateForm,
      showJoinForm,
      createGroupName,
      createGroupPassword,
      joinGroupName,
      joinGroupPassword,
      selectGroup,
      handleCreateGroup,
      handleJoinGroup,
      handleLeaveGroup,
      isPlayerTeamModalVisible,
      selectedPlayerId,
      selectedPlayerName,
      openPlayerTeamModal,
      closePlayerTeamModal,
      handleViewHorse,
      handleViewJockey,
      isHorsePerformanceModalVisible,
      selectedHorseId,
      selectedHorseName,
      openHorsePerformanceModal,
      closeHorsePerformanceModal,
      isJockeyPerformanceModalVisible,
      selectedJockeyId,
      selectedJockeyName,
      openJockeyPerformanceModal,
      closeJockeyPerformanceModal,
    };
  },
};
</script>

<style scoped>
.page-content {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
  padding: 30px 20px;
  background: linear-gradient(135deg, rgba(10, 25, 47, 0.95) 0%, rgba(17, 34, 64, 0.95) 100%);
  border-radius: 12px;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  border: 1px solid #1e3a5f;
  color: #e6f1ff;
}

.page-title {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 15px;
  color: #64ffda;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.page-description {
  font-size: 1.1rem;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  color: #a8b2d1;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.loading-spinner {
  border: 3px solid rgba(100, 255, 218, 0.3);
  border-radius: 50%;
  border-top: 3px solid #64ffda;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  color: #a8b2d1;
  font-size: 1.1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid #ff6b6b;
  border-radius: 8px;
  padding: 20px;
  margin: 30px auto;
  max-width: 800px;
  color: #ff6b6b;
  text-align: center;
}

.error-message strong {
  display: block;
  margin-bottom: 10px;
  font-size: 1.2rem;
}

.embedded-error {
  margin: 20px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #a8b2d1;
}

.empty-text {
  font-size: 1.2rem;
}

.info-text {
  text-align: center;
  color: #a8b2d1;
  font-size: 1.1rem;
  margin-bottom: 30px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.group-summary-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  background-color: #112240;
  border: 1px solid #1e3a5f;
  border-radius: 12px;
  padding: 18px 22px;
  margin-bottom: 24px;
}

.group-summary-card h2 {
  margin: 0 0 6px;
  color: #e6f1ff;
  font-size: 1.2rem;
}

.group-summary-card p {
  margin: 0;
  color: #a8b2d1;
}

.limit-pill {
  background-color: rgba(255, 184, 108, 0.12);
  border: 1px solid rgba(255, 184, 108, 0.4);
  color: #ffb86c;
  border-radius: 999px;
  padding: 8px 12px;
  font-weight: 600;
}

.group-shell {
  display: grid;
  grid-template-columns: minmax(240px, 320px) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.group-sidebar {
  background-color: #112240;
  border: 1px solid #1e3a5f;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
}

.group-sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.group-sidebar-header h2 {
  margin: 0;
  color: #e6f1ff;
  font-size: 1.3rem;
}

.group-count {
  color: #64ffda;
  font-weight: 700;
}

.group-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.group-list-item {
  width: 100%;
  background: rgba(30, 58, 95, 0.26);
  border: 1px solid #1e3a5f;
  border-radius: 10px;
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
}

.group-list-item:hover {
  border-color: rgba(100, 255, 218, 0.45);
  background: rgba(30, 58, 95, 0.42);
}

.group-list-item.selected {
  border-color: #64ffda;
  background: rgba(100, 255, 218, 0.08);
}

.group-list-name {
  display: block;
  color: #e6f1ff;
  font-weight: 600;
  margin-bottom: 10px;
}

.group-list-meta {
  display: flex;
  justify-content: flex-start;
}

.role-badge {
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.8rem;
  font-weight: 700;
}

.role-badge.owner {
  background-color: rgba(255, 184, 108, 0.12);
  color: #ffb86c;
}

.role-badge.member {
  background-color: rgba(100, 255, 218, 0.12);
  color: #64ffda;
}

.group-main {
  min-width: 0;
}

.group-content {
  margin-bottom: 30px;
}

.empty-panel {
  background-color: #112240;
  border: 1px solid #1e3a5f;
  border-radius: 12px;
  padding: 30px 20px 10px;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  margin-bottom: 30px;
}

.leaderboard-section {
  background-color: #112240;
  border: 1px solid #1e3a5f;
  border-radius: 12px;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  overflow: hidden;
  transition: transform 0.3s ease;
  margin-bottom: 30px;
}

.leaderboard-section:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 30px -15px rgba(2, 12, 27, 0.8);
}

.leaderboard-header {
  padding: 20px 25px;
  border-bottom: 1px solid #1e3a5f;
  background-color: rgba(30, 58, 95, 0.3);
}

.leaderboard-header-with-meta {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
}

.leaderboard-header h2 {
  margin: 0;
  color: #e6f1ff;
  font-size: 1.5rem;
  font-weight: 700;
  position: relative;
  padding-bottom: 10px;
}

.leaderboard-header h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 60px;
  height: 3px;
  background-color: #64ffda;
}

.leaderboard-subtitle {
  margin: 12px 0 0;
  color: #a8b2d1;
}

.table-container {
  overflow-x: auto;
  padding: 0;
}

.leaderboard-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.leaderboard-table th {
  padding: 15px 20px;
  background-color: rgba(30, 58, 95, 0.5);
  color: #a8b2d1;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.85rem;
  border-bottom: 2px solid #1e3a5f;
}

.leaderboard-row {
  transition: background-color 0.2s ease;
}

.leaderboard-row:hover {
  background-color: rgba(100, 255, 218, 0.05);
}

.leaderboard-table td {
  padding: 15px 20px;
  border-bottom: 1px solid rgba(100, 255, 218, 0.1);
  color: #e6f1ff;
}

.leaderboard-table tr:last-child td {
  border-bottom: none;
}

.rank-column {
  width: 80px;
  text-align: center;
  font-weight: 600;
  color: #a8b2d1;
}

.player-column {
  width: auto;
}

.points-column {
  width: 120px;
  text-align: right;
  font-weight: 600;
  color: #ffb86c;
}

.player-name {
  color: #e6f1ff;
  font-weight: 500;
}

.player-name.clickable {
  color: #64ffda;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 5px 8px;
  border-radius: 4px;
  margin: -5px -8px;
  display: inline-block;
}

.player-name.clickable:hover {
  background-color: rgba(100, 255, 218, 0.1);
  text-decoration: underline;
}

.player-name.disabled {
  opacity: 0.5;
  cursor: default;
}

.action-button {
  padding: 12px 24px;
  font-weight: 600;
  font-size: 1rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.action-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
}

.action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.action-button.danger {
  background-color: #ff6b6b;
  color: white;
}

.action-button.danger:hover:not(:disabled) {
  background-color: #ff5252;
}

.action-button.compact {
  padding: 10px 18px;
  font-size: 0.95rem;
}

.no-group-content {
  max-width: 800px;
  margin: 0 auto;
}

.group-actions {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.limit-message {
  background-color: rgba(255, 184, 108, 0.12);
  border: 1px solid rgba(255, 184, 108, 0.35);
  border-radius: 10px;
  padding: 14px 16px;
  color: #ffb86c;
  font-weight: 600;
}

.action-card {
  background-color: #112240;
  border: 1px solid #1e3a5f;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.action-card:hover {
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  border-color: rgba(100, 255, 218, 0.3);
}

.action-toggle {
  width: 100%;
  text-align: left;
  padding: 16px 20px;
  background-color: rgba(30, 58, 95, 0.3);
  border: none;
  color: #e6f1ff;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background-color 0.2s ease;
}

.action-toggle:hover:not(:disabled) {
  background-color: rgba(30, 58, 95, 0.5);
}

.action-toggle:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.toggle-icon {
  margin-right: 10px;
  font-size: 0.8em;
  transition: transform 0.2s ease;
}

.toggle-text {
  flex-grow: 1;
}

.action-form {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #a8b2d1;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 12px 15px;
  background-color: rgba(30, 58, 95, 0.3);
  border: 1px solid #1e3a5f;
  border-radius: 6px;
  color: #e6f1ff;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-group input:focus {
  outline: none;
  border-color: #64ffda;
  box-shadow: 0 0 0 2px rgba(100, 255, 218, 0.2);
}

.form-group input::placeholder {
  color: #8892b0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.submit-button {
  padding: 10px 20px;
  font-weight: 600;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-button.create,
.submit-button.join {
  background-color: #64ffda;
  color: #0a192f;
}

.submit-button.create:hover:not(:disabled),
.submit-button.join:hover:not(:disabled) {
  background-color: #52d6c0;
}

@media (max-width: 900px) {
  .group-shell {
    grid-template-columns: 1fr;
  }

  .group-sidebar {
    order: 2;
  }

  .group-main {
    order: 1;
  }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 1.8rem;
  }

  .page-description {
    font-size: 1rem;
  }

  .leaderboard-table th,
  .leaderboard-table td {
    padding: 12px 15px;
  }

  .leaderboard-header-with-meta {
    flex-direction: column;
    align-items: stretch;
  }

  .action-toggle {
    padding: 14px 16px;
    font-size: 1.1rem;
  }

  .group-summary-card {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .page-header {
    padding: 20px 15px;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .rank-column {
    width: 60px;
  }

  .points-column {
    width: 90px;
  }

  .leaderboard-table th,
  .leaderboard-table td {
    padding: 10px;
    font-size: 0.9rem;
  }

  .form-actions {
    justify-content: center;
  }

  .submit-button,
  .action-button.compact {
    width: 100%;
  }
}
</style>
